import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTenant } from "@/hooks/useTenant";
import { Upload, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ImportContactsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function ImportContactsDialog({ open, onOpenChange, onSuccess }: ImportContactsDialogProps) {
  const { toast } = useToast();
  const { orgId, familyId } = useTenant();
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/['"]/g, ''));
      const row: Record<string, string> = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      
      data.push(row);
    }

    return data;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
      } else {
        toast({
          title: "Invalid file",
          description: "Please select a CSV file",
          variant: "destructive",
        });
      }
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setIsUploading(true);
    try {
      if (!orgId) {
        throw new Error("Organization not loaded yet. Please try again.");
      }

      // Get the first client for this org/family
      const { data: clients } = await supabase
        .from("clients")
        .select("id")
        .eq("org_id", orgId)
        .limit(1)
        .single();

      if (!clients) {
        throw new Error("No client record found for this organization");
      }

      const text = await file.text();
      const rows = parseCSV(text);

      if (rows.length === 0) {
        throw new Error("No valid data found in CSV");
      }

      // Map CSV rows to contact records
      const contacts = rows.map(row => ({
        full_name: row.full_name || row.name || row.fullname || '',
        email: row.email || null,
        phone: row.phone || null,
        role: row.role || null,
        organization: row.org || row.organization || row.company || null,
        warmth: parseInt(row.warmth) || 5,
        client_id: clients.id,
        org_id: orgId,
        family_id: familyId,
        last_interaction: row.last_interaction || row.last_touch_at || new Date().toISOString(),
      })).filter(c => c.full_name); // Only include rows with a name

      if (contacts.length === 0) {
        throw new Error("No valid contacts found. Make sure your CSV has a 'full_name' or 'name' column");
      }

      const { error } = await supabase.from("contacts").insert(contacts);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Imported ${contacts.length} contact${contacts.length > 1 ? 's' : ''} successfully`,
      });

      setFile(null);
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      console.error("Error importing contacts:", err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to import contacts",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Import Contacts from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file with your contacts
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              CSV should have columns: full_name (required), email, phone, role, org, warmth, last_interaction
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="csv-file">Select CSV File</Label>
            <div className="flex items-center gap-2">
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={isUploading}
              />
            </div>
            {file && (
              <p className="text-sm text-muted-foreground">
                Selected: {file.name}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleImport} disabled={!file || isUploading}>
              <Upload className="h-4 w-4" />
              {isUploading ? "Importing..." : "Import Contacts"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
